import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Slide,
  Zoom,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import rsvpService from '../services/rsvpService';

const RSVPDialog = ({ open, onClose, event, onSuccess }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Build dynamic validation schema based on event's custom fields
  const validationSchema = useMemo(() => {
    const baseSchema = {
      firstName: yup.string().required('First name is required').trim(),
      lastName: yup.string().required('Last name is required').trim(),
      email: yup.string().required('Email is required').email('Invalid email').trim(),
      jobTitle: yup.string().required('Job title is required').trim(),
      phone: yup.string().required('Phone number is required').trim(),
    };

    // Add custom fields to schema
    if (event?.customFields && event.customFields.length > 0) {
      event.customFields.forEach(field => {
        let fieldValidation;

        switch (field.fieldType) {
          case 'email':
            fieldValidation = yup.string().email('Invalid email format');
            break;
          case 'number':
            fieldValidation = yup.number().typeError('Must be a number');
            break;
          case 'phone':
            fieldValidation = yup.string();
            break;
          default:
            fieldValidation = yup.string();
        }

        if (field.required) {
          fieldValidation = fieldValidation.required(`${field.fieldLabel} is required`);
        }

        if (field.fieldType !== 'number') {
          fieldValidation = fieldValidation.trim();
        }

        baseSchema[`custom_${field.fieldName}`] = fieldValidation;
      });
    }

    return yup.object(baseSchema).required();
  }, [event]);

  // Build default values including custom fields
  const defaultValues = useMemo(() => {
    const base = {
      firstName: '',
      lastName: '',
      email: '',
      jobTitle: '',
      phone: '',
    };

    if (event?.customFields && event.customFields.length > 0) {
      event.customFields.forEach(field => {
        base[`custom_${field.fieldName}`] = field.fieldType === 'number' ? '' : '';
      });
    }

    return base;
  }, [event]);

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  // Handlers
  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      // Extract standard fields
      const attendeeInfo = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        jobTitle: data.jobTitle,
        phone: data.phone,
        customFields: {},
      };

      // Extract custom field values
      if (event?.customFields && event.customFields.length > 0) {
        event.customFields.forEach(field => {
          const fieldKey = `custom_${field.fieldName}`;
          if (data[fieldKey] !== undefined && data[fieldKey] !== '') {
            attendeeInfo.customFields[field.fieldName] = data[fieldKey];
          }
        });
      }

      const rsvpData = { attendeeInfo };

      const response = await rsvpService.createRSVP(event._id, rsvpData);

      if (response.success) {
        reset();
        if (onSuccess) {
          onSuccess(response.data.rsvp);
        }
        handleClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render custom fields based on type
  const renderCustomField = (field) => {
    const fieldKey = `custom_${field.fieldName}`;
    const fieldError = errors[fieldKey];

    switch (field.fieldType) {
      case 'select':
        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field: controllerField }) => (
              <FormControl fullWidth error={!!fieldError}>
                <InputLabel required={field.required}>{field.fieldLabel}</InputLabel>
                <Select
                  {...controllerField}
                  label={field.fieldLabel}
                >
                  <MenuItem value="">
                    <em>Select an option</em>
                  </MenuItem>
                  {field.options && field.options.map((option, idx) => (
                    <MenuItem key={idx} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
              </FormControl>
            )}
          />
        );

      case 'textarea':
        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                fullWidth
                multiline
                rows={3}
                label={field.fieldLabel}
                placeholder={field.placeholder || ''}
                error={!!fieldError}
                helperText={fieldError?.message}
                required={field.required}
              />
            )}
          />
        );

      case 'number':
        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                fullWidth
                type="number"
                label={field.fieldLabel}
                placeholder={field.placeholder || ''}
                error={!!fieldError}
                helperText={fieldError?.message}
                required={field.required}
              />
            )}
          />
        );

      case 'email':
        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                fullWidth
                type="email"
                label={field.fieldLabel}
                placeholder={field.placeholder || ''}
                error={!!fieldError}
                helperText={fieldError?.message}
                required={field.required}
              />
            )}
          />
        );

      case 'phone':
      case 'text':
      default:
        return (
          <Controller
            key={fieldKey}
            name={fieldKey}
            control={control}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                fullWidth
                type={field.fieldType === 'phone' ? 'tel' : 'text'}
                label={field.fieldLabel}
                placeholder={field.placeholder || ''}
                error={!!fieldError}
                helperText={fieldError?.message}
                required={field.required}
              />
            )}
          />
        );
    }
  };

  // Guard clause
  if (!event) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Register for Event
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {event.eventName}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Error Alert */}
          {error && (
            <Zoom in timeout={300}>
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            </Zoom>
          )}

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* First Name & Last Name Row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    placeholder="John"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    required
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    placeholder="Doe"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    required
                  />
                )}
              />
            </Box>

            {/* Email */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  label="Email Address"
                  placeholder="john.doe@example.com"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                />
              )}
            />

            {/* Job Title */}
            <Controller
              name="jobTitle"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Job Title"
                  placeholder="Software Engineer"
                  error={!!errors.jobTitle}
                  helperText={errors.jobTitle?.message}
                  required
                />
              )}
            />

            {/* Phone */}
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Phone Number"
                  placeholder="+1 (555) 123-4567"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  required
                />
              )}
            />

            {/* Dynamic Custom Fields */}
            {event?.customFields && event.customFields.length > 0 && (
              <>
                {event.customFields
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map(field => renderCustomField(field))}
              </>
            )}
          </Box>

          {/* Confirmation Info Box */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'rgba(102, 126, 234, 0.08)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'rgba(102, 126, 234, 0.3)',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CheckIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                Confirmation Details
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              By submitting this form, you confirm your attendance at this event. You will receive a
              confirmation email shortly with your registration details.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? null : <CheckIcon />}
            sx={{
              minWidth: 140,
              fontWeight: 600,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: 4,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Register Now'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RSVPDialog;
